import React from "react";
import { alpha, useTheme } from "@mui/material/styles";
import {
  AccessTime,
  ArrowBack,
  CheckCircleOutline,
  CalendarMonth,
  DirectionsCar,
  ExpandLess,
  ExpandMore,
  LocationOn,
  PersonOutline,
  QueryStats,
  ShieldOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { scopertiRows } from "../constants";

function DealerResults({ activeTab, formData, quoteResult, openSections, toggleSection, onBackToPreventivi }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const ui = {
    mutedText: theme.palette.text.secondary,
    headingText: theme.palette.text.primary,
    cardBorder: alpha(theme.palette.divider, isDark ? 0.45 : 0.9),
    cardShadow: isDark ? "0 14px 34px rgba(0,0,0,0.38)" : "0 14px 34px rgba(15,23,42,0.08)",
    emptyIconBg: isDark ? alpha(theme.palette.common.white, 0.07) : alpha(theme.palette.primary.main, 0.1),
  };

  const formatCurrency = (value) =>
    value.toLocaleString("it-IT", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const renderEmptyTab = ({ title, subtitle, icon, buttonLabel }) => (
    <Box sx={{ pt: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 3,
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h3" fontWeight={800} color={ui.headingText} gutterBottom>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight={500} color={ui.mutedText}>
            {subtitle}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          onClick={onBackToPreventivi}
          startIcon={<ArrowBack />}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.2,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          Torna ai Preventivi
        </Button>
      </Box>

      <Card
        sx={{
          borderRadius: 4,
          border: `1px solid ${ui.cardBorder}`,
          boxShadow: ui.cardShadow,
          bgcolor: theme.palette.background.paper,
          minHeight: { xs: 420, md: 510 },
          display: "grid",
          placeItems: "center",
          textAlign: "center",
        }}
      >
        <CardContent sx={{ p: 4, width: "100%" }}>
          <Box
            sx={{
              width: 110,
              height: 110,
              borderRadius: "50%",
              bgcolor: ui.emptyIconBg,
              mx: "auto",
              mb: 3,
              display: "grid",
              placeItems: "center",
              color: alpha(theme.palette.primary.main, isDark ? 0.58 : 0.32),
            }}
          >
            {icon}
          </Box>

          <Typography variant="h3" fontWeight={800} color={ui.headingText} mb={1.25}>
            {title === "Storico Preventivi" ? "Nessuno storico disponibile" : "Report non disponibili"}
          </Typography>

          <Typography sx={{ fontSize: 32 / 2, color: ui.mutedText, maxWidth: 540, mx: "auto" }}>
            {title === "Storico Preventivi"
              ? "I preventivi salvati appariranno qui. Crea il tuo primo preventivo per iniziare."
              : "Le statistiche e i report appariranno qui quando avrai generato dei preventivi."}
          </Typography>

          <Button
            variant="contained"
            onClick={onBackToPreventivi}
            startIcon={buttonLabel === "Nuovo Preventivo" ? <DirectionsCar /> : <QueryStats />}
            sx={{
              mt: 3.5,
              px: 3,
              py: 1.4,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 800,
              fontSize: 16,
              boxShadow: `0 12px 22px ${alpha(theme.palette.primary.main, 0.28)}`,
            }}
          >
            {buttonLabel}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );

  if (activeTab === "storico") {
    return renderEmptyTab({
      title: "Storico Preventivi",
      subtitle: "Visualizza e gestisci i preventivi salvati",
      icon: <AccessTime sx={{ fontSize: 46 }} />,
      buttonLabel: "Nuovo Preventivo",
    });
  }

  if (activeTab === "report") {
    return renderEmptyTab({
      title: "Report e Statistiche",
      subtitle: "Analizza le performance e i dati dei preventivi",
      icon: <QueryStats sx={{ fontSize: 46 }} />,
      buttonLabel: "Inizia a Preventivare",
    });
  }

  return (
    <Box
      sx={{
        minHeight: { xs: 280, lg: 520 },
        borderRadius: 4,
        display: "block",
        color: ui.mutedText,
      }}
    >
      {quoteResult ? (
        <Stack spacing={2.5}>
          <Alert
            icon={<CheckCircleOutline fontSize="inherit" />}
            severity="success"
            variant="outlined"
            sx={{ borderRadius: 2.5, alignItems: "center" }}
          >
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ md: "center" }}>
              <Typography fontWeight={800}>Preventivo calcolato</Typography>
              <Chip size="small" icon={<LocationOn />} label={`${formData.provincia} - ${quoteResult.zone}`} />
              <Chip size="small" icon={<DirectionsCar />} label={`${formData.marca} ${formData.modello}`} />
              <Chip size="small" icon={<CalendarMonth />} label={`${formData.durata} mesi`} />
              <Chip
                size="small"
                icon={<PersonOutline />}
                label={formData.nome && formData.cognome ? `${formData.nome} ${formData.cognome}` : "Anonimo"}
              />
            </Stack>
          </Alert>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" },
              gap: 2.5,
            }}
          >
            {quoteResult.packs.map((pack) => (
              <Card
                key={pack.id}
                sx={{
                  borderRadius: 3.5,
                  border: `1px solid ${ui.cardBorder}`,
                  boxShadow: ui.cardShadow,
                  overflow: "hidden",
                }}
              >
                <Box sx={{ background: pack.gradient, color: "white", p: 3.25 }}>
                  <Stack direction="row" spacing={1.75} alignItems="center" mb={1.5}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 2,
                        bgcolor: alpha("#fff", 0.2),
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <ShieldOutlined />
                    </Box>
                    <Box>
                      <Typography variant="h4" fontWeight={800}>
                        {pack.title}
                      </Typography>
                      <Typography sx={{ color: alpha("#fff", 0.88) }}>{pack.subtitle}</Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1.25}>
                    <Chip
                      size="small"
                      label={quoteResult.zone}
                      sx={{ bgcolor: alpha("#fff", 0.18), color: "white", border: `1px solid ${alpha("#fff", 0.25)}` }}
                    />
                    <Chip
                      size="small"
                      label={`${formData.durata} mesi`}
                      sx={{ bgcolor: alpha("#fff", 0.18), color: "white", border: `1px solid ${alpha("#fff", 0.25)}` }}
                    />
                  </Stack>
                </Box>

                <CardContent sx={{ p: 3.5 }}>
                  <Typography textAlign="center" color={ui.mutedText}>
                    Premio totale
                  </Typography>
                  <Typography textAlign="center" variant="h2" fontWeight={800} color={ui.headingText} sx={{ my: 0.5 }}>
                    {formatCurrency(pack.total)} EUR
                  </Typography>
                  <Typography textAlign="center" color={ui.mutedText} mb={2.5}>
                    {formatCurrency(pack.monthly)} EUR / mese
                  </Typography>

                  <Divider sx={{ mb: 2.5 }} />
                  <Typography fontWeight={800} mb={1.5} color={ui.headingText}>
                    Coperture incluse
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1.15} mb={3}>
                    {pack.coverages.map((coverage) => (
                      <Chip
                        key={`${pack.id}-${coverage}`}
                        label={coverage}
                        variant="outlined"
                        sx={{
                          borderColor: alpha(pack.chipColor, 0.35),
                          color: pack.chipColor,
                          bgcolor: alpha(pack.chipColor, 0.08),
                          fontWeight: 700,
                        }}
                      />
                    ))}
                  </Stack>

                  <Box
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${alpha(pack.chipColor, 0.2)}`,
                      bgcolor: alpha(pack.chipColor, 0.03),
                      overflow: "hidden",
                      mb: 2.5,
                    }}
                  >
                    <Box
                      onClick={() => toggleSection(pack.id)}
                      sx={{
                        px: 2.2,
                        py: 1.6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: `1px solid ${alpha(pack.chipColor, 0.12)}`,
                        cursor: "pointer",
                      }}
                    >
                      <Box>
                        <Typography fontWeight={800} color={ui.headingText}>
                          Scoperti e Franchigie
                        </Typography>
                        <Typography color={ui.mutedText} variant="body2" fontWeight={600}>
                          Dettaglio garanzie
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => toggleSection(pack.id)}>
                        {openSections[pack.id] ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>

                    <Collapse in={Boolean(openSections[pack.id])} timeout="auto" unmountOnExit>
                      <Box sx={{ width: "100%", overflowX: "auto" }}>
                        <Box sx={{ minWidth: 620 }}>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "2.1fr 0.9fr 0.9fr 1fr",
                              px: 2.2,
                              py: 1.5,
                              gap: 1.5,
                              borderBottom: `1px solid ${alpha(pack.chipColor, 0.12)}`,
                              fontWeight: 800,
                              color: ui.headingText,
                            }}
                          >
                            <Box>Garanzia</Box>
                            <Box>Scoperto</Box>
                            <Box>Franchigia</Box>
                            <Box>Limite</Box>
                          </Box>

                          {scopertiRows.map((row) => (
                            <Box
                              key={`${pack.id}-${row.label}`}
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "2.1fr 0.9fr 0.9fr 1fr",
                                px: 2.2,
                                py: 1.5,
                                gap: 1.5,
                                alignItems: "center",
                                borderBottom: `1px solid ${alpha(pack.chipColor, 0.1)}`,
                                color: ui.headingText,
                                bgcolor: alpha(pack.chipColor, row.tone === "error" ? 0.02 : row.tone === "warning" ? 0.04 : 0.015),
                              }}
                            >
                              <Box fontWeight={700}>{row.label}</Box>
                              <Box>
                                <Chip
                                  size="small"
                                  label={row.scoperto}
                                  sx={{
                                    fontWeight: 800,
                                    bgcolor: theme.palette.background.paper,
                                    border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                                  }}
                                />
                              </Box>
                              <Box>
                                <Chip
                                  size="small"
                                  label={row.franchigia}
                                  sx={{
                                    fontWeight: 800,
                                    bgcolor: theme.palette.background.paper,
                                    border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                                  }}
                                />
                              </Box>
                              <Box
                                sx={{
                                  fontWeight: 700,
                                  color: row.tone === "warning" ? "#c2410c" : ui.mutedText,
                                  textAlign: "right",
                                }}
                              >
                                {row.limite}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Collapse>
                  </Box>

                  <Divider sx={{ mb: 2.5 }} />
                  <Stack direction="row" spacing={1.4}>
                    <Button variant="outlined" fullWidth sx={{ textTransform: "none" }}>
                      Bozza
                    </Button>
                    <Button variant="outlined" fullWidth sx={{ textTransform: "none" }}>
                      Invia
                    </Button>
                    <Button variant="contained" fullWidth sx={{ textTransform: "none", background: pack.gradient }}>
                      Emetti
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Stack>
      ) : (
        <Box textAlign="center" maxWidth={540} sx={{ mx: "auto", mt: 8 }}>
          <Box
            sx={{
              width: 106,
              height: 106,
              borderRadius: "50%",
              bgcolor: ui.emptyIconBg,
              mx: "auto",
              mb: 2,
              display: "grid",
              placeItems: "center",
              color: alpha(theme.palette.primary.main, isDark ? 0.58 : 0.32),
            }}
          >
            <DirectionsCar sx={{ fontSize: 46 }} />
          </Box>

          <Typography variant="h3" fontWeight={800} color={ui.headingText} mb={1}>
            Inizia un nuovo preventivo
          </Typography>
          <Typography sx={{ fontSize: 32 / 2 }}>
            Compila i dati del veicolo nel pannello a sinistra e clicca
            "Calcola Preventivi" per visualizzare le opzioni disponibili.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default DealerResults;
